import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { FormControlLabel, Tooltip, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import {
  Checkbox,
  Chip,
  ConfirmDialog,
  DataTable,
  IconButton,
  toast,
} from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon, ViewOffIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ArrowMoveDownRightIcon } from "@hugeicons-pro/core-stroke-standard";
import {
  DELETE_ALLOCATION,
  SET_ALLOCATION_VISIBILITY,
} from "graphql/allocation";
import AllocationEditTypeEnum from "enums/AllocationEditType";
import { useAllocationRows } from "hooks/useAllocationRows";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { DEFAULT_PAGINATION_SCROLL_LIMIT } from "components/common/Constants";
import type {
  AllocationNode,
  AllocationType,
} from "utils/allocations/allocationOptions";

export type AllocationData = AllocationNode;
export type { AllocationType };

interface AllocationTableProps {
  allocationType: AllocationType | null;
  searchTerm: string;
  /** Bumped by the page after an add or edit, so the open type reloads. */
  refreshKey: number;
  canManage: boolean;
  /** The whole type is hidden, so no row can be shown on its own. */
  typeHidden: boolean;
  /** The type's own eye, in the header above the row eyes. */
  typeVisibilityToggle?: React.ReactNode;
  /** After a delete, so the type counts follow. */
  onDeleted: () => void;
  handleOpenAllocationDrawer: (
    editType: AllocationEditTypeEnum,
    allocationToChange: AllocationData | undefined,
    allocationTypeToChange?: AllocationType,
  ) => void;
}

/** Indent for a name, one step per nesting level; search flattens the tree, so its hits sit at the first step. */
export const indentFor = (level: number, flattened: boolean): string =>
  `calc(var(--space-5) * ${flattened ? 0 : level})`;

/** One allocation type's allocations, a page at a time, on the page's own scroll. */
const AllocationTable: React.FC<AllocationTableProps> = ({
  allocationType,
  searchTerm,
  refreshKey,
  canManage,
  typeHidden,
  typeVisibilityToggle,
  onDeleted,
  handleOpenAllocationDrawer,
}) => {
  const { t } = useTranslation();
  const searching = !!searchTerm.trim();
  const { rows, loading, fetchingMore, loadMore, refetch } = useAllocationRows({
    allocationType,
    searchTerm,
  });

  // A child follows its parent, so its own eye is locked while any loaded ancestor is hidden.
  const hiddenByParent = useMemo(() => {
    const byId = new Map(rows.map((row) => [row.id, row]));
    const locked = new Set<string>();
    rows.forEach((row) => {
      for (
        let parent = row.parentAllocationId
          ? byId.get(row.parentAllocationId)
          : undefined;
        parent;
        parent = parent.parentAllocationId
          ? byId.get(parent.parentAllocationId)
          : undefined
      ) {
        if (parent.visible === false) {
          locked.add(row.id);
          return;
        }
      }
    });
    return locked;
  }, [rows]);

  const [allocationToDelete, setAllocationToDelete] =
    useState<AllocationData | null>(null);
  const [clearFromActiveCards, setClearFromActiveCards] = useState(false);
  const [deleteAllocation, { loading: deleting }] =
    useMutation(DELETE_ALLOCATION);
  const [setAllocationVisibility, { loading: settingVisibility }] = useMutation(
    SET_ALLOCATION_VISIBILITY,
  );

  useEffect(() => {
    if (refreshKey > 0) void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const changeVisibility = async (allocation: AllocationData) => {
    const visible = allocation.visible === false;
    try {
      await setAllocationVisibility({
        variables: {
          allocationTypeKey: allocation.allocationType.key,
          ids: [allocation.id],
          visible,
        },
      });
      await refetch();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(t("Failed to update allocation visibility") + ": " + message);
    }
  };

  const closeDeletePrompt = () => {
    setAllocationToDelete(null);
    setClearFromActiveCards(false);
  };

  const confirmDelete = async (allocation: AllocationData) => {
    try {
      await deleteAllocation({
        variables: {
          id: allocation.id,
          clearAllocationsFromCards: clearFromActiveCards,
        },
      });
      await refetch();
      onDeleted();
      toast.success("Allocation deleted successfully");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error("Error deleting allocation: " + message);
    }
  };

  const columns: DataColumn<AllocationData>[] = [
    {
      header: <Typography variant="label">{t("Name")}</Typography>,
      flex: true,
      cell: (allocation) => (
        <Inline
          gap="var(--space-2)"
          align="center"
          wrap={false}
          sx={{ paddingLeft: indentFor(allocation.level, searching) }}
        >
          {allocation.level > 0 && !searching && (
            <HugeiconsIcon
              icon={ArrowMoveDownRightIcon}
              size={14}
              color="var(--color-text-tertiary)"
            />
          )}
          <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
            <Inline gap="var(--space-2)" align="center" wrap={false}>
              <Typography
                variant="body1"
                sx={
                  allocation.visible === false
                    ? { color: "var(--color-text-tertiary)" }
                    : undefined
                }
              >
                {allocation.allocationCode}
              </Typography>
              {allocation.visible === false && (
                <Chip size="sm" tone="neutral">
                  {t("Hidden")}
                </Chip>
              )}
            </Inline>
            {/* Search flattens the tree, so the ancestry moves under the name. */}
            {searching && allocation.cleanedSk && (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {allocation.cleanedSk}
              </Typography>
            )}
          </Stack>
        </Inline>
      ),
    },
    ...(canManage
      ? [
          {
            header: typeVisibilityToggle ?? <span />,
            align: "right" as const,
            cell: (allocation: AllocationData) => {
              const lockedByParent = hiddenByParent.has(allocation.id);
              const hidden =
                typeHidden || lockedByParent || allocation.visible === false;
              const label = typeHidden
                ? t("Hidden")
                : lockedByParent
                  ? t("HiddenWithParent")
                  : hidden
                    ? t("Show")
                    : t("Hide");
              return (
                <Tooltip title={label} placement="top">
                  {/* The span carries the tooltip while the button is disabled. */}
                  <span>
                    <IconButton
                      variant="ghost"
                      aria-label={`${label}: ${allocation.allocationCode}`}
                      disabled={
                        typeHidden || lockedByParent || settingVisibility
                      }
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        void changeVisibility(allocation);
                      }}
                    >
                      <HugeiconsIcon
                        icon={hidden ? ViewOffIcon : ViewIcon}
                        size={18}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  // Synced allocations change only in the ledger, so they carry no menu.
  const rowActions = (allocation: AllocationData): RowAction[] =>
    canManage && allocation.editable
      ? [
          {
            label: t("Edit Allocation"),
            onClick: () =>
              handleOpenAllocationDrawer(
                AllocationEditTypeEnum.EDIT,
                allocation,
              ),
          },
          {
            label: t("Add Child Allocation"),
            onClick: () =>
              handleOpenAllocationDrawer(
                AllocationEditTypeEnum.ADD_CHILD,
                allocation,
              ),
          },
          {
            label: t("Delete Allocation"),
            danger: true,
            onClick: () => setAllocationToDelete(allocation),
          },
        ]
      : [];

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      (scrollTop + clientHeight) / scrollHeight >=
      DEFAULT_PAGINATION_SCROLL_LIMIT
    ) {
      loadMore();
    }
  };

  return (
    <>
      <DataTable<AllocationData>
        columns={columns}
        rows={rows}
        getRowKey={(allocation) =>
          `${allocation.allocationType.key}#${allocation.id}`
        }
        loading={loading}
        isFetchingMore={fetchingMore}
        loadingMoreText={t("Loading more allocations")}
        onScroll={handleScroll}
        rowActions={canManage ? rowActions : undefined}
        rowActionsLabel={t("Allocation actions")}
        emptyState={
          searching ? t("No allocations found") : t("NoAllocationsInType")
        }
        stickyHeader
      />
      {/* tone is left at its danger default, which is what this action is. */}
      <ConfirmDialog
        open={!!allocationToDelete}
        title={t("Remove Allocation")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        busy={deleting}
        onConfirm={() => {
          if (allocationToDelete) void confirmDelete(allocationToDelete);
          closeDeletePrompt();
        }}
        onCancel={closeDeletePrompt}
        message={
          <Stack gap="var(--space-5)">
            <Typography variant="body2">
              <Trans i18nKey="By deleting the allocation, you are also removing everything linked to it Do you wish to proceed?" />
            </Typography>
            <Stack gap="var(--space-4)">
              {/* The first two state what the delete takes with it; only the third is a choice. */}
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={
                  <Typography variant="body2">
                    {t("Child Allocations (required)")}
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={
                  <Typography variant="body2">
                    {t("Card Presets (required)")}
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clearFromActiveCards}
                    onChange={() => setClearFromActiveCards((value) => !value)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("Active Card Allocations?")}
                  </Typography>
                }
              />
            </Stack>
            {clearFromActiveCards && (
              <Typography variant="body2">{t("NoteAllocation")}</Typography>
            )}
          </Stack>
        }
      />
    </>
  );
};

export default AllocationTable;
